// Helper functions for BaselineComparisonView component

// Helper function to format timestep for display
// Note: Timestamps are already converted to user timezone in data processing
export const formatTimestepForDisplay = (
  timestep: string,
  aggregationMode: 'hourly' | 'daily',
  timezone?: string,
  formatInUserTimezone?: (timestamp: string, format: string) => string,
): { line1: string; line2?: string } => {
  const iso = new Date(timestep).toISOString();
  if (aggregationMode === 'daily') {
    // For daily mode, just show date
    if (timezone === 'UTC') {
      return {
        line1: new Date(timestep).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          timeZone: 'UTC',
        }),
      };
    }
    if (formatInUserTimezone) {
      return { line1: formatInUserTimezone(iso, 'MMM d') };
    }
    return {
      line1: new Date(timestep).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      }),
    };
  } else {
    // For hourly mode, show date on first line and time on second line
    if (timezone === 'UTC') {
      const datePart = new Date(timestep).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC',
      });
      const timePart = new Date(timestep)
        .toLocaleTimeString('en-US', {
          hour: 'numeric',
          hour12: true,
          timeZone: 'UTC',
        })
        .toLowerCase();
      return { line1: datePart, line2: timePart };
    }
    if (formatInUserTimezone) {
      const datePart = formatInUserTimezone(iso, 'MMM d');
      const timePart = formatInUserTimezone(iso, 'h a').toLowerCase();
      return { line1: datePart, line2: timePart };
    }
    const datePart = new Date(timestep).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
    const timePart = new Date(timestep)
      .toLocaleTimeString('en-US', {
        hour: 'numeric',
        hour12: true,
      })
      .toLowerCase();

    return {
      line1: datePart,
      line2: timePart,
    };
  }
};

// Helper function to get baseline and actual values for tooltip
export const getValuesForTooltip = (
  modelLabel: string,
  timestep: string,
  difference: number,
  baselineLabel: string,
  chartData: Array<{ time: string; [key: string]: any }>,
) => {
  // Try to get actual values from chart data
  const dataPoint = chartData.find((point) => point.time === timestep);
  if (dataPoint) {
    const baselineValue = (dataPoint[baselineLabel] as number) || 0;
    const actualValue = (dataPoint[modelLabel] as number) || 0;
    return { actualValue, baselineValue };
  }

  // Fallback to calculation from difference
  const estimatedBaselineValue = 15;
  const actualValue = estimatedBaselineValue + difference;
  return { actualValue, baselineValue: estimatedBaselineValue };
};
